// The sea trials (player-reach.md §8, R1572): the eight first acts of a new
// commander, drawn as card 06 of mockup round 075 draws them, under the
// advisor line in the queue strip.
//
// ONE line speaks for both (R2131): the advisor line carries the count of
// the sea trials as a quiet press beside its sentence, and its sentence is
// the next act of the sea trials while the advisor has no advice. A press
// on the count opens the rows under the line. This module writes the count
// and the rows; `ui/advisor.ts` writes the line.
//
// The server keeps the state. The hub sets one bit a step on the first act
// of its kind and states the mask as `Empire.seaTrials`, and
// `Empire.seaTrialsOn` says the card shows at all: a new commander's seat
// on a core world (R1566). This module turns the two into the markup and
// keeps no copy of a check, so a reload and a second device read one card.
//
// The card gives no order. The line and each SHOW button open a panel, the
// rule of the advisor line (R1305, R1307), and the player acts there.
//
// When the last step is done, one closing line drains its bar over
// `TRIALS_CLOSING_MS`, and then the card goes for good. A page that loads
// with every step done shows nothing: the closing line belongs to the
// moment the last act lands.

import { SEA_TRIALS } from '../gen/balance';
import { TRIALS_CLOSING_MS } from '../gen/presentation';
import { trials as S } from '../gen/strings';
import { wordOf } from '../locale/wordof';
import { esc } from './esc';
import { tabLabel, type Tab } from './overlay/tables';

/** How many steps the table holds. */
export const TRIAL_COUNT = SEA_TRIALS.length;

/** The mask of every step of a table of `count` rows, as an unsigned
 *  32-bit number, the form the wire states it in (R3026). */
export function trialsMask(count: number): number {
  return count >= 32 ? 0xffffffff : 2 ** count - 1;
}

/** The mask of every step. */
export const TRIALS_ALL = trialsMask(TRIAL_COUNT);

/** The query the drill row opens: the arcade honours it (R1564). */
export const DRILL_URL = '/arcade?drill=1';

/** Is step `i` done in `mask`? */
function trialDone(mask: number, i: number): boolean {
  return ((mask >>> i) & 1) === 1;
}

/** How many steps of `mask` are done. */
export function trialsDone(mask: number): number {
  let n = 0;
  for (let i = 0; i < TRIAL_COUNT; i++) if (trialDone(mask, i)) n++;
  return n;
}

/** The first step not done, in row order, or -1 when every step is. */
export function nextTrial(mask: number): number {
  for (let i = 0; i < TRIAL_COUNT; i++) if (!trialDone(mask, i)) return i;
  return -1;
}

/** The words of step `i`. The vitest holds every row to a sentence. */
export function trialWords(i: number): string {
  return wordOf(S, `step_${SEA_TRIALS[i].code}`, SEA_TRIALS[i].code);
}

/** The panel SHOW opens for step `i`: the menu and its pill. */
export function trialTarget(i: number): { tab: Tab; sub: string } {
  return { tab: SEA_TRIALS[i].tab as Tab, sub: SEA_TRIALS[i].sub };
}

/** What the card shows on one frame. */
export type TrialsStage = 'off' | 'live' | 'closing' | 'gone';

/**
 * The card's one piece of client state: whether this page saw an open step,
 * and when it saw the last one close. Everything else is the frame's.
 */
export class TrialsCard {
  private sawOpen = false;
  private closingAt: number | null = null;

  /** The stage for a frame that states `on` and `mask`, at `now` (ms). */
  stage(on: boolean, mask: number, now: number): TrialsStage {
    if (!on) return 'off';
    // `&` answers a signed number, so the compare reads it unsigned (R3026).
    if (((mask & TRIALS_ALL) >>> 0) !== TRIALS_ALL) {
      this.sawOpen = true;
      this.closingAt = null;
      return 'live';
    }
    if (!this.sawOpen) return 'gone';
    this.closingAt ??= now;
    return now - this.closingAt < TRIALS_CLOSING_MS ? 'closing' : 'gone';
  }

  /** The share of the closing bar still standing at `now`, 1 to 0. */
  closingLeft(now: number): number {
    if (this.closingAt === null) return 1;
    return Math.max(0, 1 - (now - this.closingAt) / TRIALS_CLOSING_MS);
  }
}

/** One frame of the card, as the overlay hands it over. */
export interface TrialsView {
  stage: TrialsStage;
  mask: number;
  /** The eight rows and the drill row stand open under the line. */
  open: boolean;
  /** The share of the closing bar still standing, 1 to 0. */
  left: number;
}

/** The SHOW title of step `i`: the panel it opens. */
function showTitle(i: number): string {
  const t = trialTarget(i);
  return S.show_title({ panel: `${tabLabel(t.tab)} · ${tabLabel(t.sub)}` });
}

/** The hover of the advisor line while it names the next act. */
export const TRIALS_TITLE = S.title;

/** The sentence of the next act, or '' when every act is done. */
export function trialNextLine(mask: number): string {
  const next = nextTrial(mask);
  return next < 0 ? '' : S.next({ step: trialWords(next) });
}

/** One row: the check box, the act and its SHOW button. A done row keeps
 *  its words and takes no strike, because a strike says "locked" (R45). */
function rowHtml(i: number, mask: number, next: number): string {
  const st = trialDone(mask, i) ? 'd' : i === next ? 'n' : '';
  return `<div class="trow${st ? ` ${st}` : ''}"><span class="bx${st ? ` ${st}` : ''}"></span>`
    + `<span class="tt">${esc(trialWords(i))}</span>`
    + `<button class="show" data-act="trialshow" data-n="${i}" title="${esc(showTitle(i))}">${S.show}</button></div>`;
}

/**
 * The quiet count of the sea trials on the advisor line (R2131), or ''
 * while no act is open. A press folds the rows open or shut, and the count
 * states which with `aria-expanded`.
 */
export function trialsCountHtml(v: TrialsView): string {
  if (v.stage !== 'live') return '';
  const label = S.label({ done: trialsDone(v.mask), total: TRIAL_COUNT });
  return `<button class="tcount${v.open ? ' on' : ''}" data-act="trialsfold" aria-expanded="${v.open}"`
    + ` title="${esc(v.open ? S.fold : S.unfold)}">${label}</button>`;
}

/** The drill row (R1564): it opens the drill of the arcade. */
function drillRowHtml(lead: boolean): string {
  return `<div class="trow drill${lead ? ' lead' : ''}"><span class="tt"><b>${S.drill_tag}</b> · ${S.drill}</span>`
    + `<button class="show" data-act="trialdrill" title="${esc(S.drill_title)}">${S.drill_go}</button></div>`;
}

/**
 * What stands under the advisor line, or '': the open rows of the sea
 * trials, or the closing line when the last act is done.
 *
 * The drill row stands at the head of the rows while no act is done, so a
 * new commander meets the controls first, and at their foot after (R2131).
 * The closing line keeps the class `advrow`, so a collapsed sheet keeps it
 * like the advisor line (R1555).
 */
export function trialsRowsHtml(v: TrialsView): string {
  if (v.stage === 'off' || v.stage === 'gone') return '';
  const total = TRIAL_COUNT;
  const done = trialsDone(v.mask);
  if (v.stage === 'closing') {
    return `<div class="advrow trials tr"><div class="closing"><b>${S.closing_head}</b>`
      + `<span class="long">${S.closing_long}</span>`
      + `<span class="short">${S.closing_short({ done, total })}</span>`
      + `<span class="fadebar"><i style="width:${Math.round(v.left * 100)}%"></i></span></div></div>`;
  }
  if (!v.open) return '';
  const next = nextTrial(v.mask);
  const lead = done === 0;
  let html = '<div class="tr">';
  if (lead) html += drillRowHtml(true);
  for (let i = 0; i < total; i++) html += rowHtml(i, v.mask, next);
  if (!lead) html += drillRowHtml(false);
  return `${html}</div>`;
}
